// index.jsx
import React, { useEffect, useRef, useMemo, useCallback } from "react"
import PropTypes from "prop-types"

import { useFetchMultipleFilesData } from "Shared/hooks/useFetchMultipleFilesData"
import { useOptions } from "Components/chartsLibrary/GMap/hooks"

import { initMap } from "Components/chartsLibrary/GMap/utils"
import { initScript } from "Utils/Google/initScript"
import { getObjectToExport } from "Utils/widgetsUtils"
import { exportDatas } from "Utils/global"
import html2canvas from "html2canvas"
import { saveAs } from "file-saver"
import { jsPDF } from "jspdf"

import ExportMenu from "Components/ExportMenu"
import ZoomControls from "Components/chartsLibrary/GMap/components/ZoomControls"
import { resolveTheme } from "Components/chartsLibrary/GMap/themes"

import { Container } from "Components/chartsLibrary/GMap/index.style"

const GMap = ({
  lattitudeValueData,
  longitudeValueData,
  primaryActionColor,
  centerLattitude,
  centerLongitude,
  themeGoogleMap,
  googleMapIcon,
  valueData,
  dataChart,
  useMarker,
  useMarkerCluster,
  zoomLevel,
  geoJsonFileId,
  dataNames,
  enableExportMenu,
  widgetTitle,
  fieldsData,
  languageId,
  requestLoad,
  referentialId,
}) => {
  const ref = useRef()
  const exportMenuPosition = { top: "9px", right: "9px" }
  const exportMenuId = useMemo(() => `exportMenu${Math.random()}`, [])
  const mapId = useMemo(() => `gmap${Math.random()}`, [])
  const resolvedTheme = resolveTheme(themeGoogleMap)

  const fetchMultipleFiles = useFetchMultipleFilesData()
  const { setInfoWindow, setMarkers, setBounds, setIcons, setMap, map, infoWindow } = useOptions(
    primaryActionColor,
    lattitudeValueData,
    longitudeValueData,
    googleMapIcon,
    valueData,
    dataNames,
    referentialId,
    useMarkerCluster,
    themeGoogleMap
  )

  const mapOptions = useMemo(
    () => ({
      center: { lat: centerLattitude || 48.8499198, lng: centerLongitude || 2.6370411 },
      gestureHandling: "greedy",
      zoom: zoomLevel || 8,
    }),
    [centerLattitude, centerLongitude, zoomLevel]
  )

  const loadData = useCallback(async () => {
    const data = await fetchMultipleFiles(geoJsonFileId)
    if (!data) return

    map.data.forEach(feature => map.data.remove(feature))
    for (const layer of data) {
      map.data.addGeoJson(layer)
      map.data.setStyle({
        strokeColor: primaryActionColor,
        strokeOpacity: 0.6,
        fillColor: primaryActionColor,
        fillOpacity: 0.25,
        strokeWeight: 2,
      })

      window.google.maps.event.clearListeners(map.data, "click")
      map.data.addListener("click", event => {
        const libelle = event.feature.getProperty("LIBELLE")
        if (libelle) {
          infoWindow.setContent(
            `<div style="font-family: Roboto, 'Segoe UI', sans-serif; padding: 4px 15px 10px 4px;">${libelle}</div>`
          )
          infoWindow.setPosition(event.latLng)
          infoWindow.open(map)
        }
      })
    }
  }, [fetchMultipleFiles, geoJsonFileId, map, infoWindow, primaryActionColor])

  useEffect(() => {
    const initializeMap = async () => {
      if (!window.google && ref.current) {
        await initScript()
      }
      if (ref.current) {
        initMap(
          setInfoWindow,
          setBounds,
          setIcons,
          setMap,
          primaryActionColor,
          themeGoogleMap,
          mapOptions,
          ref
        )
      }
    }
    initializeMap()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (map && ref.current) {
      map.setOptions(mapOptions)
      if (themeGoogleMap !== undefined) {
        initMap(
          setInfoWindow,
          setBounds,
          setIcons,
          setMap,
          primaryActionColor,
          themeGoogleMap,
          mapOptions,
          ref
        )
      }
    }
  }, [mapOptions, themeGoogleMap, primaryActionColor]) // eslint-disable-line react-hooks/exhaustive-deps

  const prevDataChartRef = useRef(null)
  useEffect(() => {
    if (!map) return
    if (prevDataChartRef.current === dataChart) return
    prevDataChartRef.current = dataChart

    if (useMarker && dataChart?.length) {
      setMarkers(dataChart)
    } else {
      setMarkers([])
    }
  }, [map, dataChart, useMarker, setMarkers])

  useEffect(() => {
    if (map && geoJsonFileId) {
      loadData()
    }
  }, [map, geoJsonFileId, loadData])

  const hideElement = element => {
    element.style.display = "none"
  }

  const captureMapScreenshot = () => {
    const node = document.getElementById(mapId)
    return html2canvas(node, { allowTaint: true, useCORS: true, logging: false })
  }

  const restoreExportMenu = exportMenu => {
    setTimeout(() => exportMenu.removeAttribute("style"), 10)
  }

  const exportPNG = () => {
    const title = widgetTitle.replace(/ /g, "_")
    const exportMenu = document.getElementById(exportMenuId)
    hideElement(exportMenu)
    captureMapScreenshot()
      .then(canvas => canvas.toBlob(blob => saveAs(blob, `${title}.png`)))
      .finally(() => restoreExportMenu(exportMenu))
  }

  const getImgAndSaveAsPDF = () => {
    const exportMenu = document.getElementById(exportMenuId)
    const title = widgetTitle.replace(/ /g, "_")
    exportMenu.style.display = "none"
    captureMapScreenshot()
      .then(canvas => {
        const imgData = canvas.toDataURL("image/png")
        const doc = new jsPDF()
        doc.addImage(imgData, "png", 15, 40, 180, 160)
        doc.save(`${title}.pdf`)
      })
      .finally(() => restoreExportMenu(exportMenu))
  }

  const exportMap = (fileType = "csv") => {
    if (dataChart.length) {
      const title = widgetTitle.replace(/ /g, "_")
      const header = Object.keys(dataChart[0])
      const objectToExport = getObjectToExport(
        fieldsData,
        header,
        dataChart,
        languageId,
        requestLoad
      )
      exportDatas(objectToExport, fileType, title)
    } else {
      alert("no datas to export")
    }
  }

  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }} id={mapId}>
      <Container ref={ref} />
      <ZoomControls map={map} theme={resolvedTheme} primaryActionColor={primaryActionColor} />
      {enableExportMenu && (
        <ExportMenu
          exportMenuPosition={exportMenuPosition}
          exportPNG={exportPNG}
          exportCSV={() => exportMap("csv")}
          exportXLSX={() => exportMap("xlsx")}
          exportPDF={() => exportMap("pdf")}
          exportImgPDF={getImgAndSaveAsPDF}
          exportMenuId={exportMenuId}
        />
      )}
    </div>
  )
}

GMap.propTypes = {
  themeGoogleMap: PropTypes.string,
  googleMapIcon: PropTypes.string,
  dataChart: PropTypes.array,
  useMarker: PropTypes.bool,
  useMarkerCluster: PropTypes.bool,
  geoJsonData: PropTypes.object,
  enableExportMenu: PropTypes.bool,
  widgetTitle: PropTypes.string,
  fieldsData: PropTypes.array,
  languageId: PropTypes.string,
  requestLoad: PropTypes.any,
}

GMap.defaultProps = {
  themeGoogleMap: "light",
  googleMapIcon: "type1",
  useMarker: true,
  useMarkerCluster: false,
  dataChart: [],
  geoJsonData: null,
  enableExportMenu: false,
  widgetTitle: "",
  fieldsData: [],
  languageId: "",
  requestLoad: null,
}

export default GMap
